import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  Calculator, MapPin, Loader2, Sparkles, Plus, Trash2,
  ArrowUpDown, TrendingDown, Info, CheckCircle2, RotateCcw,
  ExternalLink, CalendarDays, Plane, Hotel,
} from 'lucide-react';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY',
  'LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND',
  'OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

const emptyFacility = () => ({ name: '', city: '', state: '', procedure_price: '' });

function FacilityRow({ index, facility, onChange, onRemove, canRemove }) {
  return (
    <div
      className="grid gap-2 items-start p-4 bg-[#F0EEE9] border border-stone-200"
      style={{ gridTemplateColumns: '1fr 1fr auto auto auto' }}
      data-testid={`facility-row-${index}`}
    >
      <div>
        <label className="text-xs text-stone-500 font-semibold mb-1 block uppercase tracking-wider">
          Facility Name
        </label>
        <input
          value={facility.name}
          onChange={e => onChange('name', e.target.value)}
          placeholder="e.g. Surgery Center of Oklahoma"
          className="w-full px-3 py-2 border border-stone-200 bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A]"
          data-testid={`facility-name-${index}`}
        />
      </div>
      <div>
        <label className="text-xs text-stone-500 font-semibold mb-1 block uppercase tracking-wider">
          City
        </label>
        <input
          value={facility.city}
          onChange={e => onChange('city', e.target.value)}
          placeholder="e.g. Oklahoma City"
          className="w-full px-3 py-2 border border-stone-200 bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A]"
          data-testid={`facility-city-${index}`}
        />
      </div>
      <div>
        <label className="text-xs text-stone-500 font-semibold mb-1 block uppercase tracking-wider">
          ST
        </label>
        <select
          value={facility.state}
          onChange={e => onChange('state', e.target.value)}
          className="px-2 py-2 border border-stone-200 bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A] w-16"
          data-testid={`facility-state-${index}`}
        >
          <option value="">—</option>
          {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs text-stone-500 font-semibold mb-1 block uppercase tracking-wider">
          Price $
        </label>
        <input
          type="number"
          value={facility.procedure_price}
          onChange={e => onChange('procedure_price', e.target.value)}
          placeholder="4500"
          min="0"
          className="w-24 px-3 py-2 border border-stone-200 bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A]"
          data-testid={`facility-price-${index}`}
        />
      </div>
      <div className="flex items-end justify-center pb-1">
        {canRemove && (
          <button
            onClick={onRemove}
            className="text-stone-400 hover:text-red-500 transition-colors p-1.5"
            data-testid={`facility-remove-${index}`}
            title="Remove facility"
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

function LocalComparisonCard({ data, procedureName }) {
  if (!data) return null;
  return (
    <div
      className="bg-white border border-stone-200 p-5 mb-6 flex items-start gap-4"
      data-testid="local-comparison-card"
    >
      <div className="w-10 h-10 rounded-sm bg-[#0D3D4A]/10 flex items-center justify-center flex-shrink-0">
        <MapPin size={18} className="text-[#0D3D4A]" />
      </div>
      <div className="flex-1">
        <h3
          className="font-semibold text-stone-900 text-sm mb-1"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Stay Local in {data.state}
        </h3>
        <p className="text-sm text-stone-600">
          Average cash price for{' '}
          <em>{procedureName || 'this procedure'}</em> in {data.state}:{' '}
          <strong className="text-[#0D3D4A]">${data.avg_cash_price.toLocaleString()}</strong>
          {data.facility_count > 0 && (
            <span className="text-stone-400 text-xs ml-1.5">
              ({data.facility_count.toLocaleString()} facilities in CMS data)
            </span>
          )}
        </p>
        <p className="text-xs text-stone-400 mt-1">{data.note}</p>
      </div>
    </div>
  );
}

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function facilityKey(r) {
  return `${r.facility_name}|${r.facility_city}|${r.facility_state}`;
}

function buildBookingUrls(originCity, destCity, destState, procedureDate) {
  const flightQ = encodeURIComponent(`flights from ${originCity} to ${destCity}`);
  const hotelDest = encodeURIComponent(`${destCity} ${destState}`);
  const kayakCity = encodeURIComponent(`${destCity},${destState}`);

  let googleFlights = `https://www.google.com/flights?hl=en&q=${flightQ}`;
  let hotelsUrl = `https://www.hotels.com/search.do?q-destination=${hotelDest}`;
  let kayakUrl = `https://www.kayak.com/hotels/${kayakCity}/`;

  if (procedureDate) {
    const checkIn = procedureDate;
    const checkOut = addDays(procedureDate, 2);
    googleFlights += `&date=${procedureDate}`;
    hotelsUrl += `&q-check-in=${checkIn}&q-check-out=${checkOut}&q-rooms=1&q-room-0-adults=2`;
    kayakUrl = `https://www.kayak.com/hotels/${kayakCity}/${checkIn}/${checkOut}/`;
  }

  return { googleFlights, hotelsUrl, kayakUrl };
}

export default function TotalCostPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [patientZip, setPatientZip] = useState('');
  const [procedureName, setProcedureName] = useState('');
  const [cptCode, setCptCode] = useState('');
  const [facilities, setFacilities] = useState([emptyFacility(), emptyFacility()]);
  const [results, setResults] = useState(null);
  const [patientLocation, setPatientLocation] = useState(null);
  const [localComparison, setLocalComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [sortBy, setSortBy] = useState('total');
  const [error, setError] = useState('');
  const [procedureDate, setProcedureDate] = useState('');
  const [selectedFacilityKey, setSelectedFacilityKey] = useState(null);

  // Default selectedFacilityKey to best-value when results arrive
  useEffect(() => {
    if (results && results.length > 0) {
      const lowest = results.find(r => r.is_lowest) || results[0];
      setSelectedFacilityKey(facilityKey(lowest));
    } else {
      setSelectedFacilityKey(null);
    }
  }, [results]);

  // Pre-populate from localStorage when navigated with ?load=1
  useEffect(() => {
    if (searchParams.get('load') === '1') {
      try {
        const stored = localStorage.getItem('meridian_cost_calc_facilities');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed) && parsed.length > 0) {
            const filled = parsed.map(f => ({
              name: f.name || '',
              city: f.city || '',
              state: f.state || '',
              procedure_price: String(f.procedure_price || f.cash_price || ''),
            }));
            // pad to at least 2 rows
            while (filled.length < 2) filled.push(emptyFacility());
            setFacilities(filled);
            const first = parsed[0];
            if (first.cpt_code) setCptCode(first.cpt_code);
            if (first.procedure_description) setProcedureName(first.procedure_description);
            toast.success(`${parsed.length} facilit${parsed.length === 1 ? 'y' : 'ies'} loaded from your search.`);
          }
        }
      } catch (_) {}
    }
  }, [searchParams]);

  const updateFacility = useCallback((idx, field, value) => {
    setFacilities(prev => prev.map((f, i) => i === idx ? { ...f, [field]: value } : f));
  }, []);

  const addFacility = () => {
    if (facilities.length < 4) setFacilities(prev => [...prev, emptyFacility()]);
  };

  const removeFacility = (idx) => {
    if (facilities.length > 1) setFacilities(prev => prev.filter((_, i) => i !== idx));
  };

  const resetAll = () => {
    setFacilities([emptyFacility(), emptyFacility()]);
    setPatientZip('');
    setProcedureName('');
    setCptCode('');
    setProcedureDate('');
    setResults(null);
    setLocalComparison(null);
    setPatientLocation(null);
    setAiSuggestions([]);
    setShowAiPanel(false);
    setError('');
    setSelectedFacilityKey(null);
    localStorage.removeItem('meridian_cost_calc_facilities');
  };

  const handleAiAssist = async () => {
    if (!procedureName.trim()) {
      toast.error('Enter a procedure description first.');
      return;
    }
    setAiLoading(true);
    setShowAiPanel(false);
    try {
      const res = await axios.post(`${API}/ai/identify-procedure`, { message: procedureName });
      const procs = res.data.procedures || [];
      if (procs.length > 0) {
        setAiSuggestions(procs);
        setShowAiPanel(true);
      } else {
        toast.error('AI could not identify a procedure. Try a more specific description.');
      }
    } catch {
      toast.error('AI assist failed. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const selectSuggestion = (proc) => {
    setProcedureName(proc.name);
    setCptCode(proc.cpt || '');
    setShowAiPanel(false);
    setAiSuggestions([]);
    toast.success(`Selected: ${proc.name} (CPT ${proc.cpt})`);
  };

  const handleCalculate = async () => {
    if (!patientZip.trim() || patientZip.replace(/\D/g, '').length < 5) {
      toast.error('Please enter a valid 5-digit ZIP code.');
      return;
    }
    const validFacilities = facilities.filter(
      f => f.name.trim() && f.city.trim() && f.state && f.procedure_price
    );
    if (validFacilities.length === 0) {
      toast.error('Please add at least one complete facility (name, city, state, and price).');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);
    setLocalComparison(null);

    try {
      const payload = {
        patient_zip: patientZip.replace(/\D/g, '').slice(0, 5),
        procedure_name: procedureName || undefined,
        cpt_code: cptCode || undefined,
        facilities: validFacilities.map(f => ({
          name: f.name.trim(),
          city: f.city.trim(),
          state: f.state,
          procedure_price: parseFloat(f.procedure_price),
          cpt_code: cptCode || undefined,
        })),
      };
      const res = await axios.post(`${API}/cost/calculate`, payload);
      setResults(res.data.results || []);
      setPatientLocation(res.data.patient_location);
      setLocalComparison(res.data.local_comparison);

      setTimeout(() => {
        document.getElementById('cost-results')?.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    } catch {
      setError('Calculation failed. Please check your inputs and try again.');
    } finally {
      setLoading(false);
    }
  };

  const sortedResults = useMemo(() => {
    if (!results) return [];
    return [...results].sort((a, b) =>
      sortBy === 'total' ? a.total_cost - b.total_cost : a.procedure_price - b.procedure_price
    );
  }, [results, sortBy]);

  const selectedFacility = useMemo(() => {
    if (!sortedResults.length) return null;
    if (!selectedFacilityKey) return sortedResults.find(r => r.is_lowest) || sortedResults[0];
    return sortedResults.find(r => facilityKey(r) === selectedFacilityKey) || sortedResults[0];
  }, [sortedResults, selectedFacilityKey]);

  const bookingUrls = useMemo(() => {
    if (!selectedFacility || !patientLocation) return null;
    return buildBookingUrls(
      patientLocation.city,
      selectedFacility.facility_city,
      selectedFacility.facility_state,
      procedureDate
    );
  }, [selectedFacility, patientLocation, procedureDate]);

  const chartData = useMemo(() => {
    if (!sortedResults.length) return null;
    return {
      labels: sortedResults.map(r => {
        const n = r.facility_name || 'Unknown';
        return n.length > 28 ? n.slice(0, 25) + '…' : n;
      }),
      datasets: [
        {
          label: 'Procedure Cost',
          data: sortedResults.map(r => r.procedure_price),
          backgroundColor: '#0D3D4A',
          borderRadius: 3,
          borderSkipped: false,
        },
        {
          label: 'Travel & Lodging',
          data: sortedResults.map(r => r.total_travel_cost),
          backgroundColor: '#A8B4B8',
          borderRadius: 3,
          borderSkipped: false,
        },
      ],
    };
  }, [sortedResults]);

  const chartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: { family: 'Manrope, sans-serif', size: 11 },
          padding: 16,
          usePointStyle: true,
        },
      },
      tooltip: {
        callbacks: { label: ctx => ` $${Number(ctx.raw).toLocaleString()}` },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: v => `$${Number(v).toLocaleString()}`,
          font: { size: 10 },
        },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 11 } },
      },
    },
  };

  const chartHeight = Math.max(200, sortedResults.length * 80);

  return (
    <div className="min-h-screen bg-[#F0EEE9]">

      {/* Page Header */}
      <div className="bg-[#0D3D4A]" data-testid="cost-calc-header">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/10 flex items-center justify-center flex-shrink-0 mt-1">
              <Calculator size={22} className="text-white" />
            </div>
            <div>
              <h1
                className="text-4xl sm:text-5xl font-bold text-white mb-2 leading-tight"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                data-testid="page-title"
              >
                Total Cost of Care
              </h1>
              <p className="text-white/70 text-base max-w-2xl leading-relaxed">
                Compare the all-in cost of your procedure across multiple facilities — flight,
                lodging, ground transport, and the local alternative — so you can make the
                truly informed choice.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">

        {/* Input Panel */}
        <div
          className="bg-white border border-stone-200 p-8 mb-8"
          data-testid="cost-calc-input-panel"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your Details
            </h2>
            <button
              onClick={resetAll}
              className="flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-700 transition-colors"
              data-testid="reset-btn"
            >
              <RotateCcw size={11} /> Reset
            </button>
          </div>

          {/* ZIP + Procedure */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">
                Your ZIP Code *
              </label>
              <input
                type="text"
                value={patientZip}
                onChange={e => setPatientZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="e.g. 37201"
                maxLength={5}
                className="w-full px-4 py-3 border border-stone-200 text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] bg-[#F0EEE9]"
                data-testid="patient-zip-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">
                Procedure Name
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={procedureName}
                  onChange={e => setProcedureName(e.target.value)}
                  placeholder="e.g. gallbladder removal"
                  className="flex-1 px-4 py-3 border border-stone-200 text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] bg-[#F0EEE9]"
                  data-testid="procedure-name-input"
                />
                <button
                  onClick={handleAiAssist}
                  disabled={aiLoading}
                  className="flex items-center gap-1.5 px-4 py-3 bg-[#0D3D4A] text-white text-xs font-semibold hover:bg-[#0E4557] transition-colors disabled:opacity-60 whitespace-nowrap flex-shrink-0"
                  data-testid="ai-assist-btn"
                >
                  {aiLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  AI Assist
                </button>
              </div>
              {cptCode && (
                <p className="text-xs text-emerald-700 mt-1.5 flex items-center gap-1">
                  <CheckCircle2 size={11} /> CPT {cptCode} identified
                </p>
              )}
            </div>
          </div>

          {/* Procedure Date (optional) */}
          <div className="mb-6">
            <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <CalendarDays size={11} />
              Estimated Procedure Date
              <span className="text-stone-400 font-normal normal-case tracking-normal ml-1">
                — optional, improves booking link accuracy
              </span>
            </label>
            <input
              type="date"
              value={procedureDate}
              onChange={e => setProcedureDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full md:w-64 px-4 py-3 border border-stone-200 text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] bg-[#F0EEE9]"
              data-testid="procedure-date-input"
            />
          </div>

          {/* AI Suggestions Panel */}
          {showAiPanel && aiSuggestions.length > 0 && (
            <div
              className="mb-6 border border-[#0D3D4A]/20 bg-[#0D3D4A]/5 p-4"
              data-testid="ai-suggestions-panel"
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={13} className="text-[#0D3D4A]" />
                <p className="text-xs font-semibold text-[#0D3D4A] uppercase tracking-wider">
                  AI Procedure Suggestions
                </p>
              </div>
              <div className="space-y-2">
                {aiSuggestions.slice(0, 4).map((p, i) => (
                  <button
                    key={i}
                    onClick={() => selectSuggestion(p)}
                    className="w-full text-left flex items-start justify-between gap-3 p-3 bg-white border border-stone-200 hover:border-[#0D3D4A]/40 hover:bg-[#F0EEE9] transition-colors"
                    data-testid={`ai-suggestion-${i}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-stone-900">{p.name}</p>
                      <p className="text-xs text-stone-500 mt-0.5 line-clamp-1">{p.description}</p>
                    </div>
                    <span className="text-xs font-mono text-[#0D3D4A] bg-[#0D3D4A]/10 px-2 py-1 flex-shrink-0">
                      CPT {p.cpt}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Facility Slots */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-stone-900 mb-3 flex items-center gap-2">
              Facilities to Compare
              <span className="text-xs text-stone-400 font-normal">(up to 4)</span>
            </h3>
            <div className="space-y-3">
              {facilities.map((f, i) => (
                <FacilityRow
                  key={i}
                  index={i}
                  facility={f}
                  onChange={(field, val) => updateFacility(i, field, val)}
                  onRemove={() => removeFacility(i)}
                  canRemove={facilities.length > 1}
                />
              ))}
            </div>
            {facilities.length < 4 && (
              <button
                onClick={addFacility}
                className="mt-3 flex items-center gap-1.5 text-sm font-medium text-[#0D3D4A] hover:text-[#0E4557] transition-colors"
                data-testid="add-facility-btn"
              >
                <Plus size={15} /> Add another facility
              </button>
            )}
          </div>

          {/* Calculate Button */}
          <button
            onClick={handleCalculate}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-[#0D3D4A] text-white py-4 text-base font-semibold hover:bg-[#0E4557] transition-colors disabled:opacity-60"
            data-testid="calculate-btn"
          >
            {loading ? (
              <><Loader2 size={18} className="animate-spin" /> Calculating travel costs…</>
            ) : (
              <><Calculator size={18} /> Calculate Total Cost of Care</>
            )}
          </button>

          {error && (
            <p className="text-sm text-red-600 mt-3 flex items-center gap-1.5" data-testid="calc-error">
              <Info size={13} /> {error}
            </p>
          )}
        </div>

        {/* Disclaimer card — shown as soon as results exist */}
        {results && results.length > 0 && (
          <div
            className="mb-6 bg-[#F0EEE9] p-4"
            style={{
              borderLeft: '3px solid #0D3D4A',
              borderTop: '1px solid #D1C9BB',
              borderRight: '1px solid #D1C9BB',
              borderBottom: '1px solid #D1C9BB',
            }}
            data-testid="calc-disclaimer"
          >
            <p className="text-xs text-stone-600 leading-relaxed">
              <strong className="text-[#0D3D4A]">Estimates only.</strong>{' '}
              Travel cost estimates are based on regional averages for flights and hotels and include
              one companion. Actual costs vary based on booking timing, availability, and season.
              Always confirm procedure pricing directly with the provider before traveling.
              These estimates are for planning purposes only.
            </p>
          </div>
        )}

        {/* Results */}
        {results && results.length > 0 && (
          <div id="cost-results" data-testid="cost-results">

            {/* Patient location */}
            {patientLocation && patientLocation.city !== 'Unknown' && (
              <p className="text-xs text-stone-500 mb-4 flex items-center gap-1.5" data-testid="patient-location">
                <MapPin size={11} />
                Calculating travel from:{' '}
                <strong className="text-stone-700">{patientLocation.city}, {patientLocation.state}</strong>
                {' '}(ZIP {patientZip})
              </p>
            )}

            {/* Local comparison */}
            <LocalComparisonCard data={localComparison} procedureName={procedureName} />

            {/* Sort toggle + heading */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Cost Comparison
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-stone-500">Sort by:</span>
                <button
                  onClick={() => setSortBy(s => s === 'total' ? 'procedure' : 'total')}
                  className="flex items-center gap-1.5 text-xs font-semibold text-[#0D3D4A] border border-[#0D3D4A]/30 bg-white px-3 py-1.5 hover:bg-[#0D3D4A]/5 transition-colors"
                  data-testid="sort-toggle-btn"
                >
                  <ArrowUpDown size={11} />
                  {sortBy === 'total' ? 'Total Cost' : 'Procedure Only'}
                </button>
              </div>
            </div>

            {/* Comparison Table */}
            <div
              className="overflow-x-auto mb-8 border border-stone-200"
              data-testid="cost-comparison-table"
            >
              <table className="w-full min-w-[540px]">
                <thead>
                  <tr style={{ backgroundColor: '#0D3D4A' }}>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-white/60 uppercase tracking-wider w-36">
                      Component
                    </th>
                    {sortedResults.map((r, i) => {
                      const fk = facilityKey(r);
                      const isSelected = fk === selectedFacilityKey;
                      return (
                        <th
                          key={i}
                          onClick={() => setSelectedFacilityKey(fk)}
                          className={`py-3 px-5 text-center text-sm font-semibold min-w-[148px] cursor-pointer transition-colors ${
                            r.is_lowest ? 'text-emerald-300' : 'text-white'
                          } ${isSelected ? 'bg-white/10' : 'hover:bg-white/5'}`}
                          style={{ fontFamily: 'Outfit, sans-serif' }}
                          data-testid={`col-header-${i}`}
                          title="Click to select as booking destination"
                        >
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="block max-w-[130px] truncate" title={r.facility_name}>
                              {r.facility_name}
                            </span>
                            <span className="text-xs font-normal text-white/60">
                              {r.facility_city}, {r.facility_state}
                            </span>
                            {r.is_lowest && (
                              <span className="text-xs font-semibold text-emerald-300 bg-emerald-300/20 px-2 py-0.5 rounded-full mt-0.5">
                                Best Value
                              </span>
                            )}
                            {isSelected && (
                              <span className="text-xs font-semibold text-sky-300 bg-sky-300/20 px-2 py-0.5 rounded-full mt-0.5">
                                Selected destination
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: '#F0EEE9' }}>
                  <tr className="border-b border-stone-200">
                    <td className="py-3 px-5 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                      Procedure
                    </td>
                    {sortedResults.map((r, i) => (
                      <td key={i} className="py-3 px-5 text-center text-sm text-stone-800 font-medium"
                          data-testid={`procedure-price-${i}`}>
                        ${r.procedure_price.toLocaleString()}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-stone-200">
                    <td className="py-3 px-5 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                      Distance
                    </td>
                    {sortedResults.map((r, i) => (
                      <td key={i} className="py-3 px-5 text-center text-xs text-stone-500"
                          data-testid={`distance-${i}`}>
                        {r.distance_miles.toLocaleString()} mi
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-stone-200">
                    <td className="py-3 px-5 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                      Flight (R/T, 2 pax)
                    </td>
                    {sortedResults.map((r, i) => (
                      <td key={i} className="py-3 px-5 text-center text-sm text-stone-700"
                          data-testid={`flight-cost-${i}`}>
                        {r.flight_cost === 0 ? (
                          <span className="text-emerald-600 font-medium text-xs">Drive — no flight</span>
                        ) : (
                          `$${r.flight_cost.toLocaleString()}`
                        )}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-stone-200">
                    <td className="py-3 px-5 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                      Lodging (2 nights)
                    </td>
                    {sortedResults.map((r, i) => (
                      <td key={i} className="py-3 px-5 text-center text-sm text-stone-700"
                          data-testid={`lodging-cost-${i}`}>
                        ${r.lodging_cost.toLocaleString()}
                        <span className="block text-xs text-stone-400">
                          (${r.travel_breakdown.nightly_rate}/night)
                        </span>
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-stone-200">
                    <td className="py-3 px-5 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                      Ground Transport
                    </td>
                    {sortedResults.map((r, i) => (
                      <td key={i} className="py-3 px-5 text-center text-sm text-stone-700"
                          data-testid={`ground-transport-${i}`}>
                        {r.ground_transport === 0 ? (
                          <span className="text-emerald-600 font-medium text-xs">Included</span>
                        ) : (
                          `$${r.ground_transport.toLocaleString()}`
                        )}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b-2 border-stone-300 bg-stone-100/50">
                    <td className="py-3 px-5 text-xs font-semibold text-stone-600 uppercase tracking-wider">
                      Travel Subtotal
                    </td>
                    {sortedResults.map((r, i) => (
                      <td key={i} className="py-3 px-5 text-center text-sm font-semibold text-stone-700"
                          data-testid={`travel-subtotal-${i}`}>
                        ${r.total_travel_cost.toLocaleString()}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="py-5 px-5 text-sm font-bold text-stone-900 uppercase tracking-wider">
                      Total Cost
                    </td>
                    {sortedResults.map((r, i) => (
                      <td
                        key={i}
                        className={`py-5 px-5 text-center ${r.is_lowest ? 'bg-emerald-50' : 'bg-white'}`}
                        data-testid={`total-cost-${i}`}
                      >
                        <span
                          className={`text-2xl font-bold ${r.is_lowest ? 'text-emerald-700' : 'text-[#0D3D4A]'}`}
                          style={{ fontFamily: 'Outfit, sans-serif' }}
                        >
                          ${r.total_cost.toLocaleString()}
                        </span>
                        {r.is_lowest && (
                          <p className="text-xs text-emerald-600 font-semibold mt-0.5 flex items-center justify-center gap-1">
                            <TrendingDown size={10} /> Lowest all-in
                          </p>
                        )}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Book Your Trip — quick links for selected facility */}
            {bookingUrls && selectedFacility && (
              <div
                className="mb-8 bg-white border border-stone-200 p-6"
                data-testid="booking-links-section"
              >
                <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                  <div>
                    <h3
                      className="text-sm font-semibold text-stone-900 flex items-center gap-2"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      <Plane size={14} className="text-[#0D3D4A]" /> Book your trip to{' '}
                      <span className="text-[#0D3D4A]">
                        {selectedFacility.facility_city}, {selectedFacility.facility_state}
                      </span>
                    </h3>
                    <p className="text-xs text-stone-400 mt-1">
                      Click any facility header above to change destination.
                      {procedureDate
                        ? ` Dates pre-filled from ${procedureDate}.`
                        : ' Add a procedure date above to pre-fill travel dates.'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3" data-testid="booking-buttons">
                  <a
                    href={bookingUrls.googleFlights}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-[#0D3D4A] text-[#0D3D4A] text-xs font-semibold hover:bg-[#0D3D4A] hover:text-white transition-colors"
                    data-testid="google-flights-link"
                  >
                    <Plane size={13} />
                    Search flights on Google Flights
                    <ExternalLink size={11} className="opacity-60" />
                  </a>
                  <a
                    href={bookingUrls.hotelsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-[#0D3D4A] text-[#0D3D4A] text-xs font-semibold hover:bg-[#0D3D4A] hover:text-white transition-colors"
                    data-testid="hotels-link"
                  >
                    <Hotel size={13} />
                    Find hotels on Hotels.com
                    <ExternalLink size={11} className="opacity-60" />
                  </a>
                  <a
                    href={bookingUrls.kayakUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-[#0D3D4A] text-[#0D3D4A] text-xs font-semibold hover:bg-[#0D3D4A] hover:text-white transition-colors"
                    data-testid="kayak-link"
                  >
                    <Hotel size={13} />
                    Compare hotels on Kayak
                    <ExternalLink size={11} className="opacity-60" />
                  </a>
                </div>
                <p className="text-xs text-stone-400 mt-4 pt-4 border-t border-stone-100" data-testid="ftc-disclaimer">
                  Affiliate links — Meridian may earn a commission on bookings made through these links at no cost to you.
                </p>
              </div>
            )}

            {/* Chart */}
            {chartData && (
              <div className="bg-white border border-stone-200 p-6" data-testid="cost-chart">
                <h3
                  className="text-sm font-semibold text-stone-900 mb-1"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Cost Breakdown by Facility
                  <span className="text-xs text-stone-400 font-normal ml-2">procedure vs. travel</span>
                </h3>
                <p className="text-xs text-stone-400 mb-5">
                  Sorted by{' '}
                  <button
                    onClick={() => setSortBy(s => s === 'total' ? 'procedure' : 'total')}
                    className="underline hover:text-[#0D3D4A] transition-colors"
                    data-testid="chart-sort-toggle"
                  >
                    {sortBy === 'total' ? 'total cost' : 'procedure price only'}
                  </button>
                  {' '}— click to toggle
                </p>
                <div style={{ height: chartHeight }}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
                <p className="text-xs text-stone-400 mt-5 border-t border-stone-100 pt-4">
                  * Sorted by {sortBy === 'total' ? 'total cost' : 'procedure price'}. Click the sort
                  toggle above to reorder.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Empty state if no results yet */}
        {!results && !loading && (
          <div className="text-center py-16 text-stone-400" data-testid="empty-state">
            <Calculator size={40} className="mx-auto mb-4 opacity-30" />
            <p className="text-sm">
              Fill in your ZIP code, procedure, and up to 4 facilities above, then click{' '}
              <strong className="text-stone-500">Calculate</strong>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
